import {useCallback, useEffect, useRef, useState} from 'react'

import {announce} from '@github-ui/aria-live'
import {DEFAULT_AGENT_MODE, type AgentMode} from '@github-ui/copilot-shared-ui/utils/agent-mode'
import {useOptionalEstablishedAhpSession} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session'
import {useOptionalEstablishedAhpSessionViewState} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session-view'
import type {EstablishedAhpSession} from '@github-ui/ahp-session-client/contracts'
import {isFeatureEnabled} from '@github-ui/feature-flags'

import {useRelayAgentMode} from '../../ahp/use-relay-agent-mode'
import {useEstablishedApprovalModeMutation} from '../../hooks/use-established-approval-mode-mutation'
import {readTaskAgentMode, writeTaskAgentMode} from '../../utils/task-agent-mode-storage'
import {CLIModePicker} from './CLIModePicker'

const MODE_UPDATE_ERROR_MESSAGE = 'Your mode could not be updated. Please try again.'

interface EstablishedModeToolbarSlotProps {
  taskId: string
  disabled?: boolean
}

/**
 * Mode control for an established AHP session. AHP carries mode on the relay
 * lease, so this dispatches `session.commands.setMode` rather than the REST
 * `MODE_SWITCH` command the remote CLI profile uses.
 */
export function EstablishedModeToolbarSlot({taskId, disabled = false}: EstablishedModeToolbarSlotProps) {
  const session = useOptionalEstablishedAhpSession()
  const {approvalMode, effectiveApprovalMode, availableApprovalModes, approvalModeMutable} =
    useOptionalEstablishedAhpSessionViewState()
  const relayMode = useRelayAgentMode(taskId)
  const independentModeControlsEnabled = isFeatureEnabled('copilot_mission_control_independent_mode_controls')
  const approvalMutation = useEstablishedApprovalModeMutation()
  const [selectedMode, setSelectedMode] = useState<AgentMode>(
    () => relayMode ?? readTaskAgentMode(taskId) ?? DEFAULT_AGENT_MODE,
  )
  const [isApplying, setIsApplying] = useState(false)

  // Re-seed when a host swaps tasks without remounting, then adopt a mode
  // applied outside this control, such as the one session creation applies
  // before the composer mounts. Adjusting during render rather than in an
  // effect keeps the picker from showing another session's mode for a frame.
  const [observedTaskId, setObservedTaskId] = useState(taskId)
  const [observedRelayMode, setObservedRelayMode] = useState(relayMode)
  if (observedTaskId !== taskId) {
    setObservedTaskId(taskId)
    setObservedRelayMode(relayMode)
    setSelectedMode(relayMode ?? readTaskAgentMode(taskId) ?? DEFAULT_AGENT_MODE)
  } else if (relayMode !== observedRelayMode) {
    setObservedRelayMode(relayMode)
    if (relayMode) setSelectedMode(relayMode)
  }

  useEffect(() => {
    // Record a mode applied outside this control so reopening this session
    // recovers it. Kept out of render because a storage write is a side effect
    // and render has to stay pure to be safely replayed.
    if (relayMode) writeTaskAgentMode(taskId, relayMode)
  }, [relayMode, taskId])

  const restoredSessionRef = useRef<EstablishedAhpSession | undefined>(undefined)
  useEffect(() => {
    // The host echoes no mode, so on a cold load nothing knows which mode this
    // session is in. Restore the one this session was last recorded in, once,
    // so the picker reports a mode the session is really in. Sessions with no
    // record are left alone and shown the host's own default rather than having
    // a guess written onto them.
    if (disabled || !session || relayMode || restoredSessionRef.current === session) return
    const recorded = readTaskAgentMode(taskId)
    if (!recorded) return
    restoredSessionRef.current = session
    void (async () => {
      try {
        if (independentModeControlsEnabled) {
          await session.commands.setMode(recorded)
        } else {
          await Promise.all([
            session.commands.setMode(recorded),
            session.commands.setApproveAll(recorded === 'autopilot'),
          ])
        }
      } catch {
        // Silent: the user did not ask for this restore, and picking a mode
        // explicitly still reports its own errors.
      }
    })()
  }, [disabled, independentModeControlsEnabled, relayMode, session, taskId])

  const onModeChange = useCallback(
    (mode: AgentMode) => {
      if (!session || mode === selectedMode) return
      const previousMode = selectedMode
      setSelectedMode(mode)
      setIsApplying(true)
      void (async () => {
        try {
          if (independentModeControlsEnabled) {
            await session.commands.setMode(mode)
          } else {
            await Promise.all([session.commands.setMode(mode), session.commands.setApproveAll(mode === 'autopilot')])
          }
          writeTaskAgentMode(taskId, mode)
        } catch {
          setSelectedMode(previousMode)
          announce(MODE_UPDATE_ERROR_MESSAGE, {assertive: true})
        } finally {
          setIsApplying(false)
        }
      })()
    },
    [independentModeControlsEnabled, selectedMode, session, taskId],
  )

  return (
    <CLIModePicker
      selectedMode={selectedMode}
      onModeChange={onModeChange}
      disabled={disabled || isApplying || approvalMutation.isPending}
      selectedApprovalMode={independentModeControlsEnabled ? approvalMode : undefined}
      effectiveApprovalMode={independentModeControlsEnabled ? effectiveApprovalMode : undefined}
      availableApprovalModes={independentModeControlsEnabled ? availableApprovalModes : undefined}
      onApprovalModeChange={
        independentModeControlsEnabled && !disabled ? approvalMutation.onApprovalModeChange : undefined
      }
      approvalModeMutable={approvalModeMutable}
      approvalModePending={approvalMutation.isPending}
      approvalModeError={approvalMutation.error}
    />
  )
}
